import React from "react";
import { Search, Filter, AlertTriangle, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { StatusFilter } from "@/components/ui/status-filter";
import { ConversationStatus } from "@/types/conversation";

interface Agent {
  id: string;
  agent_name: string;
  title?: string;
}

interface InboxFiltersProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  selectedAgentId: string;
  onAgentChange: (agentId: string) => void;
  agents: Agent[];
  userTypeFilter: "all" | "leads" | "clients";
  onUserTypeChange: (value: "all" | "leads" | "clients") => void;
  statusFilter: ConversationStatus[];
  onStatusChange: (statuses: ConversationStatus[]) => void;
  showHighPriority: boolean;
  onHighPriorityChange: (show: boolean) => void;
  sentimentFilter: string;
  onSentimentChange: (sentiment: string) => void;
  conversationCount: number;
  onRefresh?: () => Promise<void>;
  isRefreshing?: boolean;
}

export const InboxFilters = ({
  searchTerm,
  onSearchChange,
  selectedAgentId,
  onAgentChange,
  agents,
  userTypeFilter,
  onUserTypeChange,
  statusFilter,
  onStatusChange,
  showHighPriority,
  onHighPriorityChange,
  sentimentFilter,
  onSentimentChange,
  conversationCount,
  onRefresh,
  isRefreshing = false,
}: InboxFiltersProps) => {
  return (
    <div className="flex-shrink-0 bg-white border-b">
      <div className="px-6 py-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Filters and Search - Left side on desktop, stacked on mobile */}
          <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-4">
            {/* Search */}
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search by name, email, intent, summary, tags..."
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                className="pl-10 w-full bg-white border-gray-300"
              />
            </div>

            {/* Agent Filter */}
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <Select value={selectedAgentId} onValueChange={onAgentChange}>
                <SelectTrigger className="w-[140px] bg-white border-gray-300">
                  <SelectValue placeholder="Filter by agent..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Agents</SelectItem>
                  {agents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.agent_name || agent.title || "Unnamed Agent"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* User Type Tabs */}
            <Tabs value={userTypeFilter} onValueChange={onUserTypeChange}>
              <TabsList className="bg-gray-100">
                <TabsTrigger value="all" className="text-sm">
                  All
                </TabsTrigger>
                <TabsTrigger value="leads" className="text-sm">
                  Leads
                </TabsTrigger>
                <TabsTrigger value="clients" className="text-sm">
                  Clients
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Status Filter */}
            <StatusFilter
              selectedStatuses={statusFilter}
              onStatusChange={onStatusChange}
            />

            {/* High Priority Filter */}
            <Button
              variant={showHighPriority ? "default" : "outline"}
              size="sm"
              onClick={() => onHighPriorityChange(!showHighPriority)}
              className="flex items-center gap-2 border-gray-300 whitespace-nowrap"
            >
              <AlertTriangle className="h-4 w-4" />
              <span className="hidden sm:inline">High Priority</span>
              <span className="sm:hidden">Priority</span>
            </Button>

            {/* Sentiment Filter */}
            <div className="flex items-center gap-2">
              <Select value={sentimentFilter} onValueChange={onSentimentChange}>
                <SelectTrigger className="w-[140px] bg-white border-gray-300">
                  <SelectValue placeholder="Sentiment" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sentiment</SelectItem>
                  <SelectItem value="positive">Positive</SelectItem>
                  <SelectItem value="neutral">Neutral</SelectItem>
                  <SelectItem value="negative">Negative</SelectItem>
                  <SelectItem value="unknown">Unknown</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Results Count - Right side on desktop, below on mobile */}
          <div className="flex items-center justify-end lg:justify-start">
            <p className="text-sm text-gray-600">
              {conversationCount} conversations
            </p>
            {onRefresh && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onRefresh}
                disabled={isRefreshing}
                className="ml-2"
              >
                {isRefreshing ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
